import React, { useRef, useState } from "react";
import { ImagePlus, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";
import ProductImg from "@/components/ProductImg";

export default function AdminProductImages({ product, onDone }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);
  const customs = product.custom_images || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/products/${product.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded — it's now this product's cover photo");
      onDone();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (fid) => {
    try {
      await api.delete(`/admin/products/${product.id}/images/${fid}`);
      toast.success("Image removed");
      onDone();
    } catch {
      toast.error("Remove failed");
    }
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/products/${product.id}/images.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(product.sku || "product").replace(/\//g, "-")}_images.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Images downloaded");
    } catch {
      toast.error("No images available to download");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" data-testid={`images-btn-${product.sku || product.id}`}>
          <ImagePlus className="h-3.5 w-3.5 mr-1" /> Images{customs.length ? ` (${customs.length})` : ""}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base leading-snug">Images — {product.name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-slate-900 hover:bg-black text-white" data-testid={`upload-image-${product.sku || product.id}`}>
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />}
            {uploading ? "Uploading…" : "Upload image"}
          </Button>
          <Button size="sm" variant="outline" onClick={downloadZip} disabled={downloading} className="rounded-full" data-testid={`download-images-${product.sku || product.id}`}>
            {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Download all (ZIP)
          </Button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`upload-input-${product.sku || product.id}`} />
        </div>
        <p className="text-xs text-slate-400">
          Uploaded images become this product's cover photo on the website. Zoho photos stay in the gallery below them.
        </p>

        {customs.length > 0 ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Uploaded on website</div>
            <div className="grid grid-cols-3 gap-3">
              {customs.map((ci) => (
                <div key={ci.fid} className="relative group rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <ProductImg src={ci.url} alt={ci.name} className="h-24 w-full object-contain p-1" />
                  <button
                    onClick={() => remove(ci.fid)}
                    data-testid={`delete-image-${ci.fid}`}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl" data-testid="no-custom-images">
            No website-uploaded images yet.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
